import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import type { AppDispatch, RootState } from '@/store'
import { startAppListening } from '@/store/listenerMiddleware'
import { openProject } from './slices/projectDetailSlice'
import { fetchProjects, projectsBusFrame } from './slices/projectsSlice'

/**
 * Reload a changed project through REST (TASK-7922504E fix cycle 1).
 *
 * `project.updated` frames carry the project's id only — the server no longer
 * puts the row on the bus, because a frame cannot be redacted per receiver
 * (`contractValue` reached client-side sockets that REST redacts it for). The
 * two slices that used to merge the frame's row now mark themselves stale
 * (`markServerChanged`) and this listener reloads what is loaded:
 *  - the open workspace's project (`openProject`), when the frame names it;
 *  - the portfolio list (`fetchProjects`), when it is loaded for the active org.
 * Each thunk's own `condition` still decides (a load already in flight is
 * vetoed and followed up once it settles — `store/cacheFollowUps.ts`). No I/O
 * here: the thunks do the loading.
 */
const refetchOnFrame = (
  action: ReturnType<typeof projectsBusFrame>,
  { getState, dispatch }: { getState: () => RootState; dispatch: AppDispatch }
): void => {
  const frame = action.payload
  const projectId = frame.project?.id
  if (frame.event !== 'project.updated' || projectId === undefined) return
  const state = getState()
  const orgId = selectActiveOrgId(state)
  if (orgId === null) return
  if (state.projectDetail.projectId === projectId) {
    void dispatch(openProject({ orgId, projectId }))
  }
  if (state.projects.loadedKey === orgId) {
    void dispatch(fetchProjects(orgId))
  }
}

/**
 * Register the listener on the shared middleware. Idempotent: RTK keeps one
 * entry per identical (actionCreator, effect) pair, and both are module constants.
 */
export const registerProjectFrameRefetch = (): void => {
  startAppListening({ actionCreator: projectsBusFrame, effect: refetchOnFrame })
}

registerProjectFrameRefetch()
